import { UpcomingFamilyEvents } from '@/pages/calendar/CalendarPage'
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Lock, User } from 'lucide-react'
import {
  useAuth,
  useFamily,
  useFamilyMembers,
  usePermissions,
  useHealthSummary,
  useSpendingSummary,
  useAlerts,
  useUnreadAlertCount,
  useFinancialConnections,
  usePlaidLinkConnect,
} from '@/hooks'
import {
  PeaceStateHero,
  AttentionNeededState,
  DetailsSummary,
  FamilyMemberCard,
  ParentWellbeingCard,
  MoneyWatchCard,
  AlertsList,
  QuickActions,
} from '@/components/dashboard'
import { SectionHeader, AnimatedCheckmark, HearthGlow } from '@/components/hearth'
import { Button } from '@/components/ui'
import { ParentSelector } from '@/components/family'
import { cn } from '@/lib/utils'
import type { Alert } from '@/types'

function getGreeting(firstName?: string): string {
  const hour = new Date().getHours()
  const base = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  return firstName ? `${base}, ${firstName}` : base
}

function getHealthStatus(summary: { metrics_by_type?: Record<string, unknown> } | undefined): {
  status: 'good' | 'unknown' | 'concern'
  metricCount: number
  description: string
} {
  if (!summary || !summary.metrics_by_type) {
    return { status: 'unknown', metricCount: 0, description: 'No data yet' }
  }

  const metricCount = Object.keys(summary.metrics_by_type).length
  if (metricCount === 0) {
    return { status: 'unknown', metricCount: 0, description: 'No metrics' }
  }

  // For now, show "good" if we have any metrics
  // In a real app, you'd analyze the metrics for concerning values
  return { status: 'good', metricCount, description: `${metricCount} metrics tracked` }
}

/** Staggered fade-up wrapper for each home section */
function Section({
  stagger,
  className,
  children,
}: {
  stagger: number
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={cn('hearth-appear', className)}
      style={{ '--stagger': stagger } as CSSProperties}
    >
      {children}
    </div>
  )
}

// Caregiver Home - greeting, hero, parents, money watch, alerts, quick actions
function CaregiverDashboard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { selectedFamily, selectedFamilyId } = useFamily()
  const { members, parentMembers, selectedMember, selectedMemberId } =
    useFamilyMembers(selectedFamilyId)
  const [detailsExpanded, setDetailsExpanded] = useState(false)

  // Real data hooks with member filtering
  const { data: healthSummary, isLoading: isLoadingHealth } = useHealthSummary(selectedFamilyId, {
    parent_user_id: selectedMember?.user.id,
    period: '7d',
  })

  const { data: spendingSummary, isLoading: isLoadingSpending } = useSpendingSummary(
    selectedFamilyId,
    '30d',
    selectedMember?.user.id
  )

  const {
    alerts,
    isLoading: isLoadingAlerts,
    resolveAlert,
    isResolving,
  } = useAlerts(selectedFamilyId, {
    status: 'UNREAD',
    member_id: selectedMemberId ?? undefined,
    page_size: 5,
  })

  const { data: unreadCount } = useUnreadAlertCount(selectedFamilyId, selectedMemberId ?? undefined)

  const firstName = user?.first_name || user?.full_name?.split(' ')[0]
  const viewingName = selectedMember?.user.full_name.split(' ')[0] || 'family member'
  const totalUnread = unreadCount?.unread_count ?? 0
  const showAttention = totalUnread > 0 && alerts.length > 0

  const healthStatus = getHealthStatus(healthSummary)

  const handleReviewAlert = (alert: Alert) => {
    navigate(`/alerts?id=${alert.id}`)
  }

  const handleDismissAlert = async (alert: Alert) => {
    await resolveAlert({
      alertId: alert.id,
      data: { resolution: 'NO_ACTION_NEEDED' },
    })
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* Greeting header with parent selector */}
      <Section stagger={0}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-medium tracking-wider text-gray-400 uppercase">
              {selectedFamily?.name ?? 'Your family'}
            </p>
            <h1 className="mt-1 font-serif text-3xl font-semibold text-gray-900">
              {getGreeting(firstName)}
            </h1>
          </div>
          <ParentSelector />
        </div>
      </Section>

      {/* Hero - peace or attention */}
      {selectedMember ? (
        <Section stagger={1} className="space-y-6">
          {showAttention ? (
            <AttentionNeededState
              alerts={alerts}
              totalCount={totalUnread}
              onReview={handleReviewAlert}
              onDismiss={handleDismissAlert}
              isDismissing={isResolving}
            />
          ) : (
            <PeaceStateHero
              memberName={viewingName}
              lastUpdated={healthSummary?.last_sync_at ?? undefined}
              onViewDetails={() => setDetailsExpanded(!detailsExpanded)}
              detailsExpanded={detailsExpanded}
            />
          )}

          <DetailsSummary
            expanded={detailsExpanded || showAttention}
            health={healthStatus}
            financial={
              spendingSummary
                ? {
                    spending: spendingSummary.total,
                    period: 'This month',
                  }
                : undefined
            }
            family={{
              memberCount: members.length,
              familyName: selectedFamily?.name || 'Your family',
            }}
            isLoading={isLoadingHealth || isLoadingSpending || isLoadingAlerts}
          />
        </Section>
      ) : (
        <Section stagger={1}>
          <div className="hearth-card p-12 text-center">
            <User className="text-primary-300 mx-auto h-12 w-12" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              Who would you like to check on?
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Pick a family member from the dropdown above to see how they're doing.
            </p>
          </div>
        </Section>
      )}

      <UpcomingFamilyEvents />

      {/* Your parents */}
      {parentMembers.length > 0 && (
        <Section stagger={2} className="space-y-3">
          <SectionHeader title={parentMembers.length === 1 ? 'Your parent' : 'Your parents'} />
          {parentMembers.map((member) => (
            <ParentWellbeingCard key={member.id} member={member} familyId={selectedFamilyId} />
          ))}
        </Section>
      )}

      {/* Money watch */}
      <Section stagger={3} className="space-y-3">
        <SectionHeader title="Money watch" />
        <MoneyWatchCard familyId={selectedFamilyId} />
      </Section>

      {/* Recent alerts */}
      {alerts.length > 0 && (
        <Section stagger={4} className="space-y-3">
          <SectionHeader
            title="Recent alerts"
            actionTitle={totalUnread > 3 ? `View all ${totalUnread}` : undefined}
            onAction={totalUnread > 3 ? () => navigate('/alerts') : undefined}
          />
          <AlertsList alerts={alerts} isLoading={isLoadingAlerts} maxItems={3} />
        </Section>
      )}

      {/* Quick actions */}
      <Section stagger={5} className="space-y-3">
        <SectionHeader title="Quick actions" />
        <QuickActions />
      </Section>
    </div>
  )
}

// Parent bank card - connection status only, never balances
function ParentBankCard({ familyId }: { familyId: string | null }) {
  const { connections, isLoading } = useFinancialConnections(familyId)
  const {
    connect,
    openPlaidLink,
    isReady,
    isLoading: isConnecting,
    error,
  } = usePlaidLinkConnect(familyId)

  // Auto-open Plaid Link when the token is ready
  useEffect(() => {
    if (isReady) {
      openPlaidLink()
    }
  }, [isReady, openPlaidLink])

  const isConnected = connections.length > 0

  if (isConnected) {
    return (
      <div className="hearth-card flex items-start gap-3 p-5">
        <span className="bg-success-500 mt-2 h-2.5 w-2.5 shrink-0 rounded-full" />
        <div>
          <p className="text-base font-medium text-gray-900">Bank account connected</p>
          <p className="mt-1 text-base text-gray-500">
            Your family is keeping watch for anything unusual
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="hearth-card space-y-4 p-5">
      <p className="text-base font-medium text-gray-900">No bank account connected</p>
      <Button
        variant="accent"
        size="lg"
        onClick={() => void connect()}
        disabled={isConnecting || isLoading || !familyId}
      >
        {isConnecting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Connecting...
          </>
        ) : (
          'Connect my bank'
        )}
      </Button>
      {error && <p className="text-error-600 text-sm">{error}</p>}
      <p className="flex items-center gap-1.5 text-sm text-gray-400">
        <Lock className="h-3.5 w-3.5 shrink-0" />
        Read-only and encrypted — no one can move your money
      </p>
    </div>
  )
}

// Personal Home - for parents viewing their own data. Gentle, larger type,
// never alarming.
function PersonalDashboard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { selectedFamilyId } = useFamily()
  const { members, isLoading: isMembersLoading } = useFamilyMembers(selectedFamilyId)

  const { data: healthSummary } = useHealthSummary(selectedFamilyId, {
    parent_user_id: user?.id,
    period: '7d',
  })

  const firstName = user?.first_name || user?.full_name?.split(' ')[0]
  const caregivers = members.filter(
    (member) => member.role === 'PRIMARY_CAREGIVER' || member.role === 'CAREGIVER'
  )

  const stepsMetric = healthSummary?.metrics?.find(
    (metric) => metric.metric_type === 'STEPS' && metric.latest_value != null
  )

  const dateLine = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      {/* Date + greeting */}
      <Section stagger={0}>
        <p className="text-xs font-medium tracking-wider text-gray-400 uppercase">{dateLine}</p>
        <h1 className="mt-1 font-serif text-4xl font-semibold text-gray-900">
          {getGreeting(firstName)}
        </h1>
      </Section>

      {/* My Day hero - always gentle, never alarming */}
      <Section stagger={1}>
        <button
          type="button"
          onClick={() => navigate('/health')}
          className="hearth-card-floating hearth-press relative w-full overflow-hidden px-6 py-10 text-center"
        >
          <HearthGlow />
          <span className="relative flex flex-col items-center">
            {stepsMetric ? (
              <>
                <span className="font-rounded text-primary-600 text-5xl font-semibold tabular-nums">
                  {Math.round(stepsMetric.latest_value!).toLocaleString()}
                </span>
                <span className="mt-1 text-lg text-gray-500">steps today</span>
                <span className="text-success-800 mt-3 font-serif text-lg font-medium">
                  You're doing wonderfully
                </span>
              </>
            ) : (
              <>
                <AnimatedCheckmark size={72} />
                <span className="mt-4 font-serif text-2xl font-semibold text-gray-900">
                  You're all set for today
                </span>
                <span className="mt-2 text-base text-gray-500">Tap to see your health info</span>
              </>
            )}
          </span>
        </button>
      </Section>

      <UpcomingFamilyEvents />

      {/* Looking out for you */}
      <Section stagger={2} className="space-y-3">
        <SectionHeader title="Looking out for you" />
        <FamilyMemberCard
          members={caregivers}
          isLoading={isMembersLoading}
          emptyMessage="Your family hasn't joined yet"
        />
      </Section>

      {/* Your bank - connection status only */}
      <Section stagger={3} className="space-y-3">
        <SectionHeader title="Your bank" />
        <ParentBankCard familyId={selectedFamilyId} />
      </Section>
    </div>
  )
}

function DashboardContent() {
  const { isParent } = usePermissions()

  // Parents see their personal dashboard
  // Caregivers see the caregiver dashboard with parent selector
  if (isParent) {
    return <PersonalDashboard />
  }

  return <CaregiverDashboard />
}

export function DashboardPage() {
  return <DashboardContent />
}
